import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import HmacSHA1 from 'crypto-js/hmac-sha1'
import ContentHeader from './ContentHeader'
import {
  useI18n,
  usePersonalInfo,
  useTalkAboutCoverage,
} from 'contexts/AuthContext'
import { Button } from 'antd'
import hourGlassIcon from './img/ic-hourglass.svg'
import { ScreenKey } from './util'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { isMobile } from 'utils/basic'
import { logBrazeEvent } from 'utils/brazeInit'
import { getLocalLanguage } from 'utils/authLocalStorage'
import { mcaScreenerPromise } from 'apis/FitOnCareAPI'
import { fitonGetProfilePromise } from 'apis/FitOnAuthAPI'
import { onCareFlowFinished } from './Rehab/util'
import { getRestrictedState } from './MCA/mcaStateGate'
import { useDeeplink } from 'hooks/useDeeplink'

export const ComingSoonReason = Object.freeze({
  INSURANCE: 1,
  MCA_STATE: 2,
})

const InsuranceComingSoon = (props) => {
  const { i18n } = useI18n()
  const { talkAboutCoverage } = useTalkAboutCoverage()
  const { personalInfo } = usePersonalInfo()

  useEffect(() => {
    const { state } = personalInfo
    logBrazeEvent('User Denied: Insurance')
    logAmplitudeEvent('Screen View: Onboarding - Not Available', {
      Reason: 'Insurance Provider',
      Detail: `${talkAboutCoverage.name}${
        talkAboutCoverage.name === 'Other'
          ? ' - ' + talkAboutCoverage.otherText
          : ''
      }${props.userStatus === 7 ? ' - No PCP' : ''}`,
      State: state?.name,
      language: getLocalLanguage(),
      sku: props.sku,
    })
  }, [talkAboutCoverage, personalInfo, props.sku, props.userStatus])

  return (
    <>
      <div
        className='subtitle'
        style={{ marginBottom: '10px', marginTop: '17px' }}
      >
        {isMobile()
          ? i18n(
              "Unfortunately, we don't accept your plan... yet! We will email you as soon as it becomes available. {0}{1} In the meantime, we offer a limited time discount for our patients when you pay out-of-pocket with your credit card.",
              <br />,
              <br />
            )
          : i18n(
              "Unfortunately, we don't accept your plan... yet! We will email you as soon as it becomes available. {0}{1} In the meantime, we offer a limited time discount for our patients when you pay out-of-pocket with your credit card.",
              '',
              ''
            )}
      </div>
      <Button
        className='btn-primary full-width-btn'
        onClick={() => {
          props.setCurrentKey(ScreenKey.CASH_PAY)
        }}
      >
        {i18n('Claim Offer - 50% OFF')}
      </Button>
      <Button
        className='btn-cancel full-width-btn font-dark m-t-24'
        onClick={() => {
          props.setCurrentKey(ScreenKey.INSURANCE_NOT_ACCEPTED_NO_CASH_PAY)
        }}
      >
        {i18n('No, thanks!')}
      </Button>
    </>
  )
}

const McaStateComingSoon = (props) => {
  const { i18n } = useI18n()
  const { from } = props
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    props.setRemoveEventListener?.(true)
    const { code, name } = getRestrictedState()
    logBrazeEvent('User Denied: State - mca')
    logAmplitudeEvent('Screen View: Onboarding - Not Available', {
      Reason: 'State',
      Detail: code,
      State: name,
      sku: 'mca',
      language: getLocalLanguage(),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onBackToApp = () => {
    setIsLoading(true)
    const finish = (fitonUserId) => {
      onCareFlowFinished({
        sku: 'mca',
        success: false,
        programId: 0,
        from,
        fitonUserId,
      })
      setIsLoading(false)
    }
    mcaScreenerPromise()
      .then((resp) => finish(resp?.data?.fitonUserId?.toString()))
      .catch(() => finish(undefined))
  }

  return (
    <>
      <div
        className='subtitle'
        style={{ marginBottom: '10px', marginTop: '17px' }}
      >
        {i18n(
          "Unfortunately, we aren't available in your state yet but we'll email you as soon as we are available in your area. Stay tuned."
        )}
      </div>
      <Button
        className='btn-primary small-btn'
        onClick={onBackToApp}
        loading={isLoading}
      >
        {i18n('Back to App')}
      </Button>
    </>
  )
}

const ComingSoon = (props) => {
  const { i18n } = useI18n()
  const navigate = useNavigate()
  const { variant } = useDeeplink()
  const { reason = ComingSoonReason.INSURANCE } = props

  const backToAboutYou = async () => {
    const resp = await fitonGetProfilePromise().catch(() => null)
    const user = resp?.data
    if (!user?.id || !user?.email) {
      navigate('/about-you', { state: { variant: variant || 'mca' } })
      return
    }
    const t = Math.floor(Date.now() / 1000).toString()
    const h = HmacSHA1(
      `${user.id}${user.email}${t}`,
      process.env.REACT_APP_autoLoginSecret
    ).toString()
    navigate('/about-you', {
      state: {
        fitonUserId: user.id,
        fitonEmail: user.email,
        variant: variant || 'mca',
        gender: user.gender,
        t,
        h,
        // CARE-166: About You shows the state that led here instead of an
        // empty State.
        stateCode: getRestrictedState().code,
      },
    })
  }

  const onBack =
    reason === ComingSoonReason.MCA_STATE ? backToAboutYou : props.onBack

  return (
    <div className='info-sec'>
      <ContentHeader {...props} onBack={onBack} />
      <div className='info-content'>
        <img src={hourGlassIcon} alt='' className='top-icon' />
        <div className='title'>{i18n('Coming soon!')}</div>
        {reason === ComingSoonReason.MCA_STATE ? (
          <McaStateComingSoon {...props} />
        ) : (
          <InsuranceComingSoon {...props} />
        )}
      </div>
    </div>
  )
}

export default ComingSoon
